import {
  AfterViewInit,
  Component,
  Inject,
  OnInit,
  PLATFORM_ID,
} from '@angular/core';
import { isPlatformBrowser, ViewportScroller } from '@angular/common';
import { NgModule } from '@angular/core';
import { ReactiveFormsModule } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatCardModule } from '@angular/material/card';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { CommonModule } from '@angular/common';

import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { FirestoreService } from '@app/core/services/firestore.service';
import { SnackbarService } from '@app/core/services/snackbar.service';

@Component({
    selector: 'app-contact',
    imports: [
        CommonModule,
        ReactiveFormsModule,
        MatFormFieldModule,
        MatInputModule,
        MatSelectModule,
        MatCheckboxModule,
        MatButtonModule,
        MatIconModule,
        MatCardModule,
        MatProgressSpinnerModule,
    ],
    templateUrl: './contact.component.html',
    styleUrl: './contact.component.css'
})
export class ContactComponent implements OnInit, AfterViewInit {
  contactForm: FormGroup;
  selectedFileName: string | null = null;
  isLoading = false;

  reasons = [
    'General Inquiry',
    'Partnership / Collaboration',
    'Careers & Internships',
    'Media / Press',
    'Technical Support',
  ];

  faqs = [
    {
      q: 'How long does it take to get a response?',
      a: 'We usually respond within 24–48 hours. For career inquiries or complex partnership proposals, it may take up to a week.',
    },
    {
      q: 'Can I collaborate as a freelancer or partner?',
      a: 'Absolutely! We are always looking for "Angular lovers" to join the journey. Select “Partnership / Collaboration” in the form and tell us about your expertise.',
    },
    {
      q: 'Where is your team based?',
      a: 'We are headquartered at WeWork Two Horizon, Gurugram—the heart of India’s tech hub. Our engineering roots stem from IIM Kozhikode and Delhi College of Engineering (DCE) alumni, serving a global community.',
    },
    {
      q: 'Is Angularize.dev free to use?',
      a: 'During our current beta phase, we are offering early access to a select group of builders. Reach out via "General Inquiry" to request a spot in our next cohort.',
    },
    {
      q: 'How does Angularize differ from generic AI code generators?',
      a: 'Unlike generic LLMs, Angularize enforces enterprise-grade architecture and Angular Material best practices out of the box. It’s built for developers who want production-ready code, not just snippets.',
    },
    {
      q: 'Can I request a custom feature or integration?',
      a: 'Yes! We prioritize our roadmap based on user feedback. If you have a specific enterprise requirement, choose "Technical Support" and let us know.',
    },
  ];

  constructor(
    private fb: FormBuilder,
    private scroller: ViewportScroller,
    private firestoreService: FirestoreService,
    private snack: SnackbarService,
  ) {
    this.contactForm = this.fb.group({
      fullName: ['', Validators.required],
      email: ['', [Validators.required, Validators.email]],
      company: [''],
      reason: ['', Validators.required],
      message: ['', Validators.required],
      attachment: [null],
      consent: [false, Validators.requiredTrue],
    });
  }

  ngOnInit() {
    // this.viewportScroller.scrollToPosition([0, 0]);
  }
  ngAfterViewInit() {
    // Only run this if we are in the browser, not on the server!
    setTimeout(() => {
      this.scroller.scrollToPosition([0, 0]);
    }, 10);
  }

  onFileSelected(event: Event) {
    const input = event.target as HTMLInputElement;
    const file = input.files && input.files[0];

    if (file) {
      if (file.type !== 'application/pdf') {
        alert('Please upload a PDF file only.');
        return;
      }

      this.selectedFileName = file.name;
      this.contactForm.patchValue({ attachment: file });
    }
  }

  async onSubmit() {
    if (this.contactForm.valid) {
      this.isLoading = true; // Start Loader

      try {
        const { fullName, email, company, reason, message, attachment } =
          this.contactForm.value;
        let fileUrl = null;

        // 1. Handle File Upload
        if (attachment) {
          const path = `enquiries/${Date.now()}_${attachment.name}`;
          fileUrl = await this.firestoreService.uploadFile(path, attachment);
        }

        // 2. Save to Firestore
        const enquiry = {
          fullName,
          email,
          company: company || 'N/A',
          reason,
          message,
          attachmentUrl: fileUrl,
          timestamp: new Date(),
        };

        await this.firestoreService.addData('enquiries', enquiry);

        // Success Feedback
        this.snack.showSuccess('Message sent! We will get back to you soon.');
        this.contactForm.reset();
        this.selectedFileName = null;
      } catch (err) {
        console.error(err);
        this.snack.showError('Failed to send message. Please try again later.');
      } finally {
        this.isLoading = false; // Stop Loader
      }
    } else {
      this.contactForm.markAllAsTouched();
      this.snack.showInfo('Please fill in all required fields.');
    }
  }
}
